import { KeyValue } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { createMask } from '@ngneat/input-mask';
import { ComplianceSettingForm } from 'src/app/classes/form-groups/compliance-setting-form';
import { Company } from 'src/app/classes/models';
import { ComplianceSettingLiabilityType } from 'src/app/interfaces/compliance-setting-liability-types';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-compliance-workers-compensation',
  templateUrl: './compliance-workers-compensation.component.html',
  styleUrls: ['./compliance-workers-compensation.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ComplianceWorkersCompensationComponent {
  constructor(private UtilityService: UtilityService) {}

  @Input() complianceSettingForms?: ComplianceSettingForm[];
  @Input() company?: Company;
  @Input() amBestValueMap?: Map<string, string | number | null>;
  @Input() amBestFinancialValueMap?: Map<
    string,
    { value: string | null; hint: string }
  >;
  @Input() amountPerOccurrenceMap?: Map<string, number | null>;
  @Input() amountAggregateMap?: Map<string, number | null>;

  @Output() updateRequiredFields =
    new EventEmitter<ComplianceSettingLiabilityType>();

  checkBoxFields?: Map<
    string,
    { controlName: string; show: boolean; labelText?: string }
  >;

  collapse: boolean = true;

  eachIncidentEmployersLimitMap: Map<string, number | null> =
    this.UtilityService.setCoverageAmounts(
      100000,
      50000,
      20000000,
      new Map([['Not Required', null]]),
      new Map([['$20,000,000+', 20000001]])
    );
  eachEmployeeMinimumMap: Map<string, number | null> =
    this.eachIncidentEmployersLimitMap;
  eachEmployerMinimumMap: Map<string, number | null> =
    this.eachIncidentEmployersLimitMap;

  currencyMask = createMask({
    alias: 'numeric',
    groupSeparator: ',',
    placeholder: '',
    rightAlign: false
  });

  // This model takes the inverse of the checkbox selection per compliance setting form,
  // since we display the question inversely.
  ComplianceSettingToDisallowWCExemptionsModel = new Map<
    ComplianceSettingForm,
    boolean
  >([]);

  getPolicyHeaderText() {
    const TextParts = [];
    if (this.company) {
      const { CompanyOperationStates, country } = this.company;

      if (CompanyOperationStates) {
        const OperationStateSet = new Set(
          CompanyOperationStates.map(
            (cos) => cos.SubNationalDivision?.abbreviation
          )
        );
        if (country === 'CA') {
          const hasWSIB = OperationStateSet.has('ON');
          const hasWCB = OperationStateSet.has('AB');

          const WCTextParts = [];
          if (hasWSIB) {
            WCTextParts.push('WSIB');
          }

          if (hasWCB) {
            WCTextParts.push('WCB');
          }

          TextParts.push(WCTextParts.join(' / '));

          if (TextParts.length === 0) {
            TextParts.push("Canadian Worker's Compensation");
          }
        } else {
          TextParts.push("Worker's Compensation");
        }
      }
    }

    return TextParts.join('');
  }

  // The currency mask parser doesn't work. So, we'll handle it ourselves.
  applyCurrencyFormat(FormControl: FormControl, value: any) {
    FormControl.patchValue(
      value === null ? null : parseInt(value.replace(/[^\d]/g, ''), 10),
      { emitViewToModelChange: false }
    );
  }

  keyValuePairDefaultOrder(
    a: KeyValue<any, any>,
    b: KeyValue<any, any>
  ): number {
    return 0;
  }

  onWorkersCompensationIsRequiredChange() {
    this.updateRequiredFields.emit('workersCompensation');
  }

  disallowWCExemptionsModelChange(
    ComplianceSettingForm: ComplianceSettingForm,
    value: boolean
  ) {
    ComplianceSettingForm.patchValue({ disallowWCExemptions: !value });
  }

  recalculateDisallowWCExemptions() {
    this.complianceSettingForms!.forEach((ComplianceSettingForm) => {
      const {
        value: { disallowWCExemptions }
      } = ComplianceSettingForm;
      this.ComplianceSettingToDisallowWCExemptionsModel.set(
        ComplianceSettingForm,
        !disallowWCExemptions
      );
    });
  }

  ngOnChanges(SimpleChanges: SimpleChanges) {
    const { complianceSettingForms } = SimpleChanges;

    if (complianceSettingForms) {
      this.recalculateDisallowWCExemptions();
    }
  }

  ngOnInit(): void {
    if (!this.complianceSettingForms) {
      throw new Error('Missing compliance setting forms.');
    }

    this.updateRequiredFields.emit('workersCompensation');

    this.checkBoxFields = new Map([
      ['Waiver of Subrogation', { controlName: 'wkIsAgency', show: true }],
      [
        'Valid Description of Operations',
        { controlName: 'wkDescOps', show: true }
      ],
      [
        'Has Required Forms',
        { controlName: 'workersCompensation_hasRequiredForms', show: true }
      ],
      [
        'Officer Exclusion',
        {
          controlName: 'workersCompensation_officerExclusionNotAllowed',
          show: true,
          labelText: 'Allowed?'
        }
      ],
      [
        'COI Name/Legal Name Mismatch',
        { controlName: 'workersCompensation_legalNameMismatch', show: true }
      ]
    ]);

    this.recalculateDisallowWCExemptions();
  }
}
